import React, { useEffect, useReducer } from 'react';
import styled from 'styled-components';

import { formatRelativeTime } from '@/features/course/learner/utils/formatRelativeTime';
import { IconButton } from '@/ui/Button';
import { Icon } from '@/ui/Icon';
import { Tooltip } from '@/ui/Tooltip';

/** "just now" has to turn into "a minute ago" while the learner is still on the page. */
const TIMESTAMP_REFRESH_INTERVAL_MS = 30 * 1000;

const RootStyled = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 16px 20px;
  border: 1px solid ${(p) => p.theme.colors.neutral.outline1};
  border-radius: 8px;
  background-color: ${(p) => p.theme.tones.neutral[99]};
`;

const FileIconStyled = styled(Icon)`
  flex-shrink: 0;
  font-size: 24px;
  color: ${(p) => p.theme.colors.primary.main};
`;

const DetailsStyled = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

/* The legacy global stylesheet still puts em-based margins on paragraphs */
const FileNameStyled = styled.p`
  ${(p) => p.theme.typography.title5};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${(p) => p.theme.colors.neutral.onBackground};
`;

const StatusStyled = styled.p`
  ${(p) => p.theme.typography.caption};
  margin: 0;
  color: ${(p) => p.theme.colors.neutral.onContainer};
`;

const ActionsStyled = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 4px;
`;

type CourseAssignmentFileCardProps = {
  fileName: string;
  fileUrl: string;
  /** Set once the file is submitted. A submitted file can be replaced, but no longer removed. */
  submittedAt?: Date;
  onReplace: VoidFunction;
  onRemove: VoidFunction;
  /** While the file is being submitted — changing it then would be overwritten by the answer. */
  actionsDisabled?: boolean;
  className?: string;
};

/**
 * The learner's uploaded PDF in the assignment submission box, before and after Submit.
 * The actions are icon-only, so each carries its own accessible name instead of relying on
 * the tooltip.
 */
export const CourseAssignmentFileCard = ({
  fileName,
  fileUrl,
  submittedAt,
  onReplace,
  onRemove,
  actionsDisabled = false,
  className,
}: CourseAssignmentFileCardProps): JSX.Element => {
  const [, refreshTimestamp] = useReducer((tick: number) => tick + 1, 0);

  useEffect(() => {
    if (!submittedAt) return;

    const interval = setInterval(refreshTimestamp, TIMESTAMP_REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [submittedAt]);

  return (
    <RootStyled className={className}>
      <FileIconStyled icon={['fas', 'file-pdf']} aria-hidden />
      <DetailsStyled>
        <FileNameStyled title={fileName}>{fileName}</FileNameStyled>
        <StatusStyled>{submittedAt ? `Submitted ${formatRelativeTime(submittedAt)}` : 'Not submitted yet'}</StatusStyled>
      </DetailsStyled>
      <ActionsStyled>
        <Tooltip title="View" placement="top">
          <IconButton
            icon={['far', 'eye']}
            buttonVariant="buddySmall"
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="View file"
          />
        </Tooltip>
        <Tooltip title="Replace" placement="top">
          <IconButton
            icon={['far', 'arrows-rotate']}
            buttonVariant="buddySmall"
            type="button"
            onClick={onReplace}
            disabled={actionsDisabled}
            aria-label="Replace file"
          />
        </Tooltip>
        {!submittedAt && (
          <Tooltip title="Remove" placement="top">
            <IconButton
              icon={['far', 'trash']}
              buttonVariant="buddySmall"
              type="button"
              onClick={onRemove}
              disabled={actionsDisabled}
              aria-label="Remove file"
            />
          </Tooltip>
        )}
      </ActionsStyled>
    </RootStyled>
  );
};
